/* eslint-disable no-magic-numbers */
import { Color } from '@technology/web3d';
import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  DoubleSide,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Points,
  PointsMaterial,
  ShapeUtils,
  Vector2,
} from '@technology/web3d-plugin-threejs';
import { getLogger } from '@trimble/logging';
import { toLocalPositions } from './local-positions';
import type { LayerItem } from './maps-api/types/layer.types';

// Constants
const LINE_WIDTH = 2;
const MIN_LINESTRING_POINTS = 2;
const MIN_POLYGON_POINTS = 3;
const POINT_SIZE = 10;
const CLUSTER_POINT_SIZE = 40;
const POSITION_COMPONENTS = 3;
const CLUSTER_OPACITY = 0.2;
const FULL_OPACITY = 1.0;
const DEFAULT_POLYGON_FILL_OPACITY = 0.2;
const OPACITY_PERCENT_DIVISOR = 100;
const RGB_HEX_LENGTH = 6;
const ARGB_HEX_LENGTH = 8;
const OPAQUE_ALPHA = 0;
const MAX_ALPHA = 255;
const FEATURE_GEOMETRY_RENDER_ORDER = 999;

// Default colors used when no layer template color is available
const DEFAULT_POINT_COLOR = 'red';
const DEFAULT_LINE_COLOR = 'blue';
const DEFAULT_POLYGON_COLOR = 'yellow';
const CLUSTER_COLOR = '#ffffff';

export const FEATURE_MESH_USER_DATA_ID_KEY = 'featureId';
export const FEATURE_MESH_USER_DATA_COLOR_KEY = 'featureDisplayColor';
export const FEATURE_MESH_USER_DATA_CLUSTER_KEY = 'isClusterFeature';

export function stampFeatureMeshUserData(
  mesh: Object3D,
  featureId: string,
  displayColor: string
): void {
  mesh.userData ??= {};
  mesh.userData[FEATURE_MESH_USER_DATA_ID_KEY] = featureId;
  mesh.userData[FEATURE_MESH_USER_DATA_COLOR_KEY] = displayColor;
}

export interface ParsedGeometryColor {
  /** Three.js-compatible `#rrggbb` color string. */
  color: string;
  /** Normalised opacity in [0, 1], or null when alpha is unset (6-digit input or ARGB alpha byte '00'). */
  opacity: number | null;
}

/**
 * Parses a layer color value into a color string and an optional opacity.
 * Accepts:
 * - 6-digit RGB hex string (opacity always null)
 * - 8-digit ARGB hex string (alpha byte '00' → null; otherwise normalised to [0, 1])
 * - packed integer (e.g. from LayerStyle.lineColor / fillColor): values ≤ 0xFFFFFF
 *   are treated as 24-bit RGB; larger values as 32-bit ARGB.
 * Returns null for missing or malformed values.
 */
export function parseGeometryColor(
  hex: string | number | null | undefined
): ParsedGeometryColor | null {
  if (hex == null || hex === '') {
    return null;
  }

  let hexString: string;
  if (typeof hex === 'number') {
    const unsigned = hex >>> 0;
    hexString =
      unsigned <= 0xffffff
        ? unsigned.toString(16).padStart(RGB_HEX_LENGTH, '0')
        : unsigned.toString(16).padStart(ARGB_HEX_LENGTH, '0');
  } else {
    hexString = hex;
  }

  const trimmed = hexString.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]+$/.test(trimmed)) {
    return null;
  }

  if (trimmed.length === RGB_HEX_LENGTH) {
    return { color: `#${trimmed.toLowerCase()}`, opacity: null };
  }

  if (trimmed.length === ARGB_HEX_LENGTH) {
    const alpha = parseInt(
      trimmed.slice(0, ARGB_HEX_LENGTH - RGB_HEX_LENGTH),
      16
    );
    return {
      color: `#${trimmed.slice(ARGB_HEX_LENGTH - RGB_HEX_LENGTH).toLowerCase()}`,
      opacity: alpha === OPAQUE_ALPHA ? null : alpha / MAX_ALPHA,
    };
  }

  return null;
}

/**
 * Updates the `color` property on every material found in the mesh subtree.
 * Covers PointsMaterial, LineBasicMaterial, and MeshBasicMaterial without
 * requiring callers to import a Three.js Color themselves.
 */
export function setFeatureMeshColor(mesh: Object3D, colorHex: string): void {
  const color = new Color(colorHex);
  mesh.traverse((obj) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- material type varies by mesh subclass (Points/Line/Mesh)
    const mat = (obj as any).material as
      | { color?: unknown; needsUpdate: boolean }
      | undefined;
    if (mat && mat.color instanceof Color) {
      mat.color.copy(color);
      mat.needsUpdate = true;
    }
  });
}

// Coordinate indices for GeoJSON
export enum CoordinateIndex {
  LONGITUDE = 0,
  LATITUDE = 1,
  ELEVATION = 2,
}

// Types
export interface FeatureMesh {
  mesh: Object3D;
  featureId: string;
  metadata?: Record<string, unknown>;
  /**
   * Original GeoJSON feature this mesh was built from. Carried so the model can
   * emit feature ingestion events (e.g. to mirror features into line editing).
   */
  feature?: GeoJsonFeature;
}

// Specific geometry types with proper coordinate typing
export type PointGeometry = {
  type: 'Point';
  coordinates: [number, number, number?];
};

export type LineStringGeometry = {
  type: 'LineString';
  coordinates: [number, number, number?][];
};

export type PolygonGeometry = {
  type: 'Polygon';
  coordinates: [number, number, number?][][];
};

export type GeometryType = PointGeometry | LineStringGeometry | PolygonGeometry;

export interface GeoJsonFeature {
  type: 'Feature';
  id: string;
  geometry: GeometryType;
  properties: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface GeoJsonCollection {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
  eTags?: Record<string, string>;
  total?: number;
  startIndex?: number;
  pageSize?: number;
}

export interface Position {
  x: number;
  y: number;
  /** Scene Z to use when the input included a height (including ellipsoidal fallback). */
  z?: number;
}

/**
 * Transforms GeoJSON [lon, lat, elevation?] to scene coordinates. The elevation is a
 * WGS84 ellipsoidal height; when present, scene Z is returned as `z`.
 */
export type CoordinateTransformFn = (
  coordinates: [number, number, number?]
) => Position;

/**
 * Returns the surface elevation at a scene X,Y, or undefined when
 * unavailable. Used to drop cluster and elevation-less point meshes onto the
 * loaded surface instead of the z=0 plane.
 */
export type ElevationSampler = (x: number, y: number) => number | undefined;

/**
 * Translates GeoJSON features to Three.js BufferGeometry meshes.
 */
export class FeaturesBufferGeomTranslator {
  private circleTexture: CanvasTexture | null = null;
  private readonly logger = getLogger();

  constructor(
    private readonly coordinateTransform: CoordinateTransformFn,
    private readonly elevationSampler?: ElevationSampler
  ) {}

  private resolveElevation(
    explicit: number | undefined,
    position: Position
  ): number {
    if (explicit !== undefined) {
      return explicit;
    }
    return this.elevationSampler?.(position.x, position.y) ?? 0;
  }

  /**
   * Creates a BufferGeometry mesh from a GeoJSON feature.
   * @param layer - Optional layer metadata used to style the mesh (template geometry color).
   */
  public createFeatureMesh(
    feature: GeoJsonFeature,
    layer?: LayerItem
  ): FeatureMesh | null {
    switch (feature.geometry.type) {
      case 'Point':
        return this.createPointMesh(
          feature as GeoJsonFeature & { geometry: PointGeometry },
          layer
        );

      case 'LineString':
        return this.createLineStringMesh(
          feature as GeoJsonFeature & { geometry: LineStringGeometry },
          layer
        );

      case 'Polygon':
        return this.createPolygonMesh(
          feature as GeoJsonFeature & { geometry: PolygonGeometry },
          layer
        );

      default:
        this.logger.warn(
          `Unsupported geometry type: ${(feature.geometry as GeometryType).type}`
        );
        return null;
    }
  }

  /**
   * Creates multiple BufferGeometry meshes from a GeoJSON collection.
   * @param layersById - Optional lookup of layer metadata keyed by layer ID. Each
   * feature is matched via its `metadata.common_layerId` to resolve per-layer styling.
   */
  public createFeatureMeshes(
    collection: GeoJsonCollection,
    layersById?: ReadonlyMap<string, LayerItem>
  ): FeatureMesh[] {
    if (!collection?.features?.length) {
      // console.warn('No features found in the provided GeoJSON collection.');
      return [];
    }

    const meshes: FeatureMesh[] = [];

    for (const feature of collection.features) {
      if (!feature.geometry.coordinates?.length) {
        this.logger.warn(`[SKIPPING] ${feature.id} - is missing coordinates`);
        continue;
      }

      const layerId = feature.metadata?.['common_layerId'];
      const layer =
        typeof layerId === 'string' ? layersById?.get(layerId) : undefined;

      const featureMesh = this.createFeatureMesh(feature, layer);
      if (featureMesh) {
        meshes.push(featureMesh);
      }
    }

    return meshes;
  }

  /**
   * Disposes of a mesh's geometry and material.
   */
  public static disposeMesh(mesh: Object3D): void {
    // Recursively dispose Group children
    if ('isGroup' in mesh && mesh.isGroup) {
      mesh.children.forEach((child) =>
        FeaturesBufferGeomTranslator.disposeMesh(child)
      );
      return;
    }
    // Type guard to check if mesh has geometry and material
    if ('geometry' in mesh && mesh.geometry) {
      (mesh.geometry as BufferGeometry).dispose();
    }
    if ('material' in mesh && mesh.material) {
      const material = mesh.material as unknown;
      if (Array.isArray(material)) {
        material.forEach(
          (mat: { map?: { dispose(): void }; dispose(): void }) => {
            mat.map?.dispose();
            mat.dispose();
          }
        );
      } else if (
        material &&
        typeof material === 'object' &&
        'dispose' in material
      ) {
        const disposable = material as {
          map?: { dispose(): void };
          dispose(): void;
        };
        disposable.map?.dispose();
        disposable.dispose();
      }
    }
  }

  private createPointMesh(
    feature: GeoJsonFeature & { geometry: PointGeometry },
    layer?: LayerItem
  ): FeatureMesh | null {
    if (feature.metadata?.['cluster']) {
      return this.createClusterMesh(feature);
    }

    const coords = feature.geometry.coordinates;
    const position = this.coordinateTransform([
      coords[CoordinateIndex.LONGITUDE],
      coords[CoordinateIndex.LATITUDE],
      coords[CoordinateIndex.ELEVATION],
    ]);
    const elevation = this.resolveElevation(
      position.z ?? coords[CoordinateIndex.ELEVATION],
      position
    );

    const parsed = parseGeometryColor(
      layer?.template?.geometryColorHexRGB ?? layer?.style?.lineColor
    );
    const color = parsed?.color ?? DEFAULT_POINT_COLOR;
    const opacity = parsed?.opacity ?? FULL_OPACITY;

    const { origin, local } = toLocalPositions([
      position.x,
      position.y,
      elevation,
    ]);
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new BufferAttribute(local, POSITION_COMPONENTS)
    );

    const isTransparent = opacity < FULL_OPACITY;
    const material = new PointsMaterial({
      color: new Color(color),
      size: POINT_SIZE,
      sizeAttenuation: false,
      transparent: isTransparent,
      opacity,
      map: this.getCircleTexture(),
      // When transparent, full alpha blending handles the circle disc cutout.
      // When opaque, alpha blending is disabled so a non-zero alphaTest is
      // required to discard the outside-circle fragments via the opaque pass.
      alphaTest: isTransparent ? 0 : 0.5,
      depthTest: false,
      depthWrite: false,
    });
    const points = new Points(geometry, material);
    points.position.set(origin.x, origin.y, 0);
    points.renderOrder = FEATURE_GEOMETRY_RENDER_ORDER;
    stampFeatureMeshUserData(points, feature.id, color);

    return {
      mesh: points,
      featureId: feature.id,
      metadata: feature.metadata,
      feature,
    };
  }

  private createClusterMesh(
    feature: GeoJsonFeature & { geometry: PointGeometry }
  ): FeatureMesh | null {
    const coords = feature.geometry.coordinates;
    const position = this.coordinateTransform([
      coords[CoordinateIndex.LONGITUDE],
      coords[CoordinateIndex.LATITUDE],
      coords[CoordinateIndex.ELEVATION],
    ]);
    // Clusters carry no elevation, so this samples the surface at
    // the cluster's X,Y (falling back to 0) instead of pinning it to z=0.
    const elevation = this.resolveElevation(
      position.z ?? coords[CoordinateIndex.ELEVATION],
      position
    );

    const { origin, local } = toLocalPositions([
      position.x,
      position.y,
      elevation,
    ]);
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new BufferAttribute(local, POSITION_COMPONENTS)
    );

    const material = new PointsMaterial({
      color: new Color(CLUSTER_COLOR),
      size: CLUSTER_POINT_SIZE,
      sizeAttenuation: false,
      transparent: true,
      opacity: CLUSTER_OPACITY,
      map: this.getCircleTexture(),
      alphaTest: 0,
      depthTest: false,
      depthWrite: false,
    });
    const points = new Points(geometry, material);
    points.position.set(origin.x, origin.y, 0);
    points.renderOrder = FEATURE_GEOMETRY_RENDER_ORDER;
    stampFeatureMeshUserData(points, feature.id, CLUSTER_COLOR);
    points.userData[FEATURE_MESH_USER_DATA_CLUSTER_KEY] = true;

    return {
      mesh: points,
      featureId: feature.id,
      metadata: feature.metadata,
      feature,
    };
  }

  private createLineStringMesh(
    feature: GeoJsonFeature & { geometry: LineStringGeometry },
    layer?: LayerItem
  ): FeatureMesh | null {
    const coordinates = feature.geometry.coordinates;

    if (coordinates.length < MIN_LINESTRING_POINTS) {
      this.logger.warn(
        `LineString feature ${feature.id} requires at least ${MIN_LINESTRING_POINTS} points`
      );
      return null;
    }

    const positions: number[] = [];
    for (const coord of coordinates) {
      const position = this.coordinateTransform([
        coord[CoordinateIndex.LONGITUDE],
        coord[CoordinateIndex.LATITUDE],
        coord[CoordinateIndex.ELEVATION],
      ]);
      const elevation = this.resolveElevation(
        position.z ?? coord[CoordinateIndex.ELEVATION],
        position
      );
      positions.push(position.x, position.y, elevation);
    }
    const { origin, local } = toLocalPositions(positions);

    // Create BufferGeometry for line
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new BufferAttribute(local, POSITION_COMPONENTS)
    );

    // Create material
    const lineParsed = parseGeometryColor(
      layer?.template?.geometryColorHexRGB ?? layer?.style?.lineColor
    );
    const lineOpacity = lineParsed?.opacity ?? FULL_OPACITY;
    const material = new LineBasicMaterial({
      color: new Color(lineParsed?.color ?? DEFAULT_LINE_COLOR),
      linewidth: LINE_WIDTH,
      transparent: lineOpacity < FULL_OPACITY,
      opacity: lineOpacity,
      depthTest: false,
      depthWrite: false,
    });

    const line = new Line(geometry, material);
    line.position.set(origin.x, origin.y, 0);
    line.renderOrder = FEATURE_GEOMETRY_RENDER_ORDER;
    stampFeatureMeshUserData(
      line,
      feature.id,
      lineParsed?.color ?? DEFAULT_LINE_COLOR
    );

    return {
      mesh: line,
      featureId: feature.id,
      metadata: feature.metadata,
      feature,
    };
  }

  private createPolygonMesh(
    feature: GeoJsonFeature & { geometry: PolygonGeometry },
    layer?: LayerItem
  ): FeatureMesh | null {
    const outerRing = feature.geometry.coordinates[0];

    if (!outerRing || outerRing.length < MIN_POLYGON_POINTS) {
      this.logger.warn(
        `Polygon feature ${feature.id} requires at least ${MIN_POLYGON_POINTS} points`
      );
      return null;
    }

    const positions: number[] = [];
    for (const coord of outerRing) {
      const position = this.coordinateTransform([
        coord[CoordinateIndex.LONGITUDE],
        coord[CoordinateIndex.LATITUDE],
        coord[CoordinateIndex.ELEVATION],
      ]);
      const elevation = this.resolveElevation(
        position.z ?? coord[CoordinateIndex.ELEVATION],
        position
      );
      positions.push(position.x, position.y, elevation);
    }
    const { origin, local } = toLocalPositions(positions);

    // Create BufferGeometry for polygon outline
    const outlineGeometry = new BufferGeometry();
    outlineGeometry.setAttribute(
      'position',
      new BufferAttribute(local, POSITION_COMPONENTS)
    );

    const outlineParsed = parseGeometryColor(
      layer?.template?.geometryColorHexRGB ?? layer?.style?.lineColor
    );
    const outlineColor = outlineParsed?.color ?? DEFAULT_POLYGON_COLOR;
    const outlineOpacity = outlineParsed?.opacity ?? FULL_OPACITY;

    const outlineMaterial = new LineBasicMaterial({
      color: new Color(outlineColor),
      linewidth: LINE_WIDTH,
      transparent: outlineOpacity < FULL_OPACITY,
      opacity: outlineOpacity,
      depthTest: false,
      depthWrite: false,
    });

    const line = new Line(outlineGeometry, outlineMaterial);
    line.renderOrder = FEATURE_GEOMETRY_RENDER_ORDER;
    line.computeLineDistances(); // Required for dashed lines if needed

    // Create fill geometry using earcut triangulation via ShapeUtils.triangulateShape.
    // This correctly handles both convex and concave (non-convex) polygons, unlike a
    // simple triangle fan which can produce overlapping or exterior-crossing triangles
    // for concave shapes (e.g. L-shaped parcels, irregular boundaries).
    const fillGeometry = new BufferGeometry();
    fillGeometry.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(local), POSITION_COMPONENTS)
    );

    // GeoJSON rings close with first === last vertex; strip the closing duplicate
    // before passing to triangulateShape so earcut sees only unique vertices.
    // Guard against non-spec-compliant rings that omit the closing duplicate by
    // only stripping the last vertex when it actually equals the first.
    const vertexCount = positions.length / POSITION_COMPONENTS;
    const lastMatchesFirst =
      positions[(vertexCount - 1) * POSITION_COMPONENTS] === positions[0] &&
      positions[(vertexCount - 1) * POSITION_COMPONENTS + 1] === positions[1];
    const uniqueVertexCount = lastMatchesFirst ? vertexCount - 1 : vertexCount;
    const contour: Vector2[] = [];
    for (let i = 0; i < uniqueVertexCount; i++) {
      contour.push(
        new Vector2(
          local[i * POSITION_COMPONENTS],
          local[i * POSITION_COMPONENTS + 1]
        )
      );
    }
    const faces = ShapeUtils.triangulateShape(contour, []);
    fillGeometry.setIndex(faces.flat());

    const fillParsed = parseGeometryColor(
      layer?.template?.geometryColorHexRGB ??
        layer?.style?.fillColor ??
        layer?.style?.lineColor
    );
    const fillColorParsed = fillParsed?.color ?? DEFAULT_POLYGON_COLOR;
    const fillOpacityParsed =
      layer?.style?.fillOpacity != null
        ? layer.style.fillOpacity / OPACITY_PERCENT_DIVISOR
        : (fillParsed?.opacity ?? DEFAULT_POLYGON_FILL_OPACITY);

    const fillMaterial = new MeshBasicMaterial({
      color: new Color(fillColorParsed),
      transparent: true,
      opacity: fillOpacityParsed,
      side: DoubleSide,
      depthTest: false,
      depthWrite: false,
    });

    const fillMesh = new Mesh(fillGeometry, fillMaterial);
    fillMesh.renderOrder = FEATURE_GEOMETRY_RENDER_ORDER;

    const group = new Group();
    group.position.set(origin.x, origin.y, 0);
    group.renderOrder = FEATURE_GEOMETRY_RENDER_ORDER;
    group.add(line);
    group.add(fillMesh);
    stampFeatureMeshUserData(group, feature.id, outlineColor);

    return {
      mesh: group,
      featureId: feature.id,
      metadata: feature.metadata,
      feature,
    };
  }

  // Converts buffGeom squares to svg circles
  private getCircleTexture(): CanvasTexture {
    if (!this.circleTexture) {
      this.circleTexture = this.createCircleTexture();
    }
    return this.circleTexture;
  }

  private createCircleTexture(): CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;

    const context = canvas.getContext('2d');
    if (context === null) {
      throw new Error('Failed to create 2D canvas context');
    }

    // Clear canvas to transparent
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Draw solid white circle
    context.beginPath();
    context.arc(32, 32, 30, 0, Math.PI * 2);
    context.fillStyle = '#ffffff';
    context.fill();

    return new CanvasTexture(canvas);
  }
}
